import usePairedDevice from "@/hooks/usePairedDevice";
import { Checkmark } from "@carbon/icons-react";
import { alpha, Avatar, Box, Button, Typography, useTheme, Container } from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import BoltProductImage from "../BoltProductImage";
import { WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";
import { Trans } from "@lingui/macro";
import { useBCFirstRunState } from "@/hooks/useBCFirstRunState";
import { WSMBoltAppFirstRunState } from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import { ErrorBoundary, GlobalHeader, postRoute } from "@WahooFitness/wahoo-offline-mfe";

interface OnboardingCompleteProps {
  appToken: string;
}

const OnboardingComplete: React.FC<OnboardingCompleteProps> = ({ appToken }) => {
  const { device, isDeviceConnected } = usePairedDevice(appToken);
  const { palette } = useTheme();

  const { setStateTransition } = useBCFirstRunState(appToken);
  useEffect(() => {
    setStateTransition({
      desiredState: WSMBoltAppFirstRunState.Complete,
    });
  }, [setStateTransition]);

  const computerName = useMemo(() => device?.productTypeName, [device]);

  const goHome = useCallback(() => {
    postRoute("home");
  }, []);

  return (
    <>
      <GlobalHeader
        primaryRightActionItem={{
          icon: <WfIconNavigationCloseRegular />,
          action: goHome,
        }}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box display="flex" flexDirection="column" height="100%">
          <ErrorBoundary isLoading={!isDeviceConnected} error={false}>
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              alignItems="center"
              justifyContent="space-between"
              height="100%"
            >
              <Box display="flex" flexDirection="column" width="100%" alignItems="center">
                <Box m={3}>
                  <BoltProductImage boltType={device?.boltType} appToken={appToken} height={200} />
                </Box>
                <Box display="flex" flexDirection="column" gap={1} m={3} alignItems="center">
                  <Avatar
                    sx={{ width: "72px", height: "72px", bgcolor: alpha(palette.info.main, 0.1) }}
                  >
                    <Checkmark size="56px" color={palette.info.main} />
                  </Avatar>
                  <Typography textAlign="center" variant="prose-lg-bold">
                    <Trans>Setup Done!</Trans>
                  </Typography>
                  <Typography textAlign="center" variant="prose-sm">
                    <Trans>Your {computerName} is ready to go!</Trans>
                  </Typography>
                </Box>
              </Box>
              <Box px={4} mb={4} width="100%">
                <Button variant="contained" size="large" fullWidth onClick={goHome}>
                  <Trans>Complete</Trans>
                </Button>
              </Box>
            </Box>
          </ErrorBoundary>
        </Box>
      </Container>
    </>
  );
};

export { OnboardingComplete };
